import { RefObject, useCallback, useEffect } from 'react';
import {
  SelectionSyncMessageType,
  SelectionSyncSide,
  isPartSelectedMessage,
  isClearSelectionMessage,
} from '@physna/core/domain/viewer/selection-sync-messaging';
import { getIframeMessagingContext } from './getIframeMessagingContext';

export function useSelectionSync({
  iframeRef,
  onViewerSelect,
  onViewerClear,
}: {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onViewerSelect: (assetIds: string[], selectionPath?: string[], side?: SelectionSyncSide) => void;
  onViewerClear: (side?: SelectionSyncSide) => void;
}) {
  const sendSelectionToViewer = useCallback(
    (assetIds: string[], selectionPath?: string[], side?: SelectionSyncSide) => {
      const messagingContext = getIframeMessagingContext(iframeRef);
      if (!messagingContext) return;

      messagingContext.iframeWindow.postMessage(
        { type: SelectionSyncMessageType.SelectParts, src: 'parent', assetIds, selectionPath, side },
        messagingContext.targetOrigin
      );
    },
    [iframeRef]
  );

  const sendClearToViewer = useCallback(
    (side?: SelectionSyncSide) => {
      const messagingContext = getIframeMessagingContext(iframeRef);
      if (!messagingContext) return;

      messagingContext.iframeWindow.postMessage(
        { type: SelectionSyncMessageType.ClearSelection, src: 'parent', side },
        messagingContext.targetOrigin
      );
    },
    [iframeRef]
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const messagingContext = getIframeMessagingContext(iframeRef);
      if (!messagingContext) return;
      if (event.source !== messagingContext.iframeWindow || event.origin !== messagingContext.targetOrigin) return;

      const data = event.data as unknown;

      if (isPartSelectedMessage(data)) {
        if (data.src === 'parent') return;
        onViewerSelect(data.assetIds, data.selectionPath, data.side);
        return;
      }

      if (isClearSelectionMessage(data)) {
        if (data.src === 'parent') return;
        onViewerClear(data.side);
      }
    };

    globalThis.addEventListener('message', onMessage);
    return () => globalThis.removeEventListener('message', onMessage);
  }, [iframeRef, onViewerSelect, onViewerClear]);

  return { sendSelectionToViewer, sendClearToViewer };
}
